import {useEffect, useMemo, useRef, useState} from 'react'
import {initialQueryState, QueryState} from '../../_metronic/helpers'
import { FilterIcon } from 'lucide-react'

export type FilterFieldProps = {
  name: string
  label: string
  options?: {value: any; label: string}[]
  type?: 'select' | 'date' | 'text'
  searchable?: boolean
}

type Props = {
  updateState: (updates: Partial<QueryState>) => void
  isLoading: boolean
  fields: (FilterFieldProps | string)[]
  mode?: 'apply' | 'instant'
}
export const Filter = ({updateState, isLoading, fields, mode = 'apply'}: Props) => {
  const [values, setValues] = useState<Record<string, any>>({})
  const [optionSearch, setOptionSearch] = useState<Record<string, string>>({})
    const [isOpen, setIsOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    const toggleMenu = () => setIsOpen((prev) => !prev)

   const closeMenu = () => setIsOpen(false)

   useEffect(() => {
     const handleClickOutside = (event: MouseEvent) => {
       if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
         closeMenu()
       }
     }
     document.addEventListener('mousedown', handleClickOutside)
     return () => {
       document.removeEventListener('mousedown', handleClickOutside)
     }
   }, [])

  const resetData = () => {
    setValues({})
    updateState({filter: undefined, ...initialQueryState})
  }
  const filterData = () => {
    updateState({
      filter: {...values},
      ...initialQueryState,
    })
  }

  const setValue = (name: string, value: any) => {
    setValues((prev) => {
      const next = {...prev}

      // Empty value => treat as clearing that single filter field
      if (value === '' || value === undefined || value === null) {
        delete next[name]
      } else {
        next[name] = value
      }
      if (mode === 'instant') {
        updateState({filter: next, ...initialQueryState})
        closeMenu()
      }
      return next
    })
  }
  const sampleFields = useMemo(() => {
    const res: Record<string, FilterFieldProps> = {
      status: {
        name: 'status',
        label: 'Status',
        options: [
          {value: true, label: 'Active'},
          {value: false, label: 'Inactive'},
        ],
      },
    }

    return res
  }, [])

  return (
    <div className='relative inline-block' ref={menuRef}>
      <button
        disabled={isLoading}
        type='button'
        className='flex items-center gap-1 text-sm px-4 py-2 bg-[#ccf2f2] text-[#008080] rounded-md hover:bg-[#b3e6e6] disabled:opacity-50'
        onClick={toggleMenu}
      >
        {/* Replace KTSVG with icon if desired */}
        <FilterIcon className='w-4 h-4' />
        Filter
      </button>

      {isOpen && (
        <div className='absolute right-0 mt-2 w-[300px] md:w-[325px] bg-white rounded-lg shadow-lg border border-gray-200 z-50'>
          <div className='px-6 py-4'>
            <div className='text-base font-semibold text-gray-800'>Filter Options</div>
          </div>

          <div className='border-t border-gray-200'></div>

          <div className='px-6 py-4 space-y-6'>
            {fields.map((f, index) => {
              let field: FilterFieldProps
              if (typeof f === 'string') field = sampleFields[f]
              else field = f

              if (!field) return null

              return (
                <div key={index}>
                  <label className='block text-sm font-semibold text-gray-700 mb-1'>
                    {field.label}:
                  </label>
                  {field.type === 'text' ? (
                    <input
                      type='text'
                      className='w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2'
                      value={values[field.name] ?? ''}
                      onChange={(e) => setValue(field.name, e.target.value)}
                      placeholder={`Filter by ${field.label}`}
                    />
                  ) : field.type === 'date' ? (
                    <input
                      type='date'
                      className='w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2'
                      value={values[field.name] ?? ''}
                      onChange={(e) => setValue(field.name, e.target.value)}
                    />
                  ) : (
                    <div className='space-y-2'>
                      {(field.searchable || (field.options?.length || 0) > 10) ? (
                        <input
                          type='text'
                          className='w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2'
                          value={optionSearch[field.name] ?? ''}
                          onChange={(e) =>
                            setOptionSearch((prev) => ({...prev, [field.name]: e.target.value}))
                          }
                          placeholder={`Search ${field.label}`}
                        />
                      ) : null}

                    <select
                      className='w-full rounded-md border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2 bg-white'
                      onChange={(e) => {
                        const selectedValue =
                          e.target.value === 'true'
                            ? true
                            : e.target.value === 'false'
                              ? false
                              : e.target.value
                        setValue(field.name, selectedValue)
                      }}
                      value={
                        values[field.name] === true
                          ? 'true'
                          : values[field.name] === false
                            ? 'false'
                            : values[field.name] ?? ''
                      }
                    >
                      <option value=''>All</option>
                      {Array.isArray(field.options) &&
                        field.options
                          .filter((opt) => {
                            const q = (optionSearch[field.name] ?? '').trim().toLowerCase()
                            if (!q) return true
                            return String(opt.label || '').toLowerCase().includes(q)
                          })
                          .map((option, idx) => (
                            <option key={idx} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                    </select>
                    </div>
                  )}
                </div>
              )
            })}

            {mode === 'apply' ? (
              <div className='flex justify-end gap-2'>
                <button
                  type='button'
                  disabled={isLoading}
                  onClick={resetData}
                  className='flex items-center gap-1 text-sm px-4 py-2 bg-[#ccf2f2] text-[#008080] rounded-md hover:bg-[#b3e6e6] disabled:opacity-50'
                >
                  Reset
                </button>
                <button
                  disabled={isLoading}
                  type='button'
                  onClick={filterData}
                  className='px-4 py-2 rounded-md bg-[#008080] hover:bg-[#006666] text-white text-sm font-semibold disabled:opacity-50'
                >
                  Apply
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}
